import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Vote, Plus, X, Check } from "lucide-react";

export default function Polls() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/polls");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const clean = options.map((o) => o.trim()).filter(Boolean);
    if (clean.length < 2) return toast.error("Add at least 2 options");
    setSaving(true);
    try {
      await api.post("/polls", { question: q, options: clean });
      toast.success("Poll created");
      setQ(""); setOptions(["", ""]); setOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create");
    } finally {
      setSaving(false);
    }
  };

  const vote = async (id, idx) => {
    await api.post(`/polls/${id}/vote`, { option_index: idx });
    toast.success("Vote recorded");
    load();
  };

  return (
    <div className="space-y-6" data-testid="polls-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
            <Vote className="w-4 h-4" /> Community voice
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Polls & votes</h1>
        </div>
        {isBoard && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-11" data-testid="new-poll-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New poll
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a poll</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="poll-form">
                <div>
                  <Label>Question</Label>
                  <Input required value={q} onChange={(e) => setQ(e.target.value)} className="mt-1.5 h-11" data-testid="poll-question-input" placeholder="Should we upgrade the pool?" />
                </div>
                <div>
                  <Label>Options</Label>
                  <div className="space-y-2 mt-1.5">
                    {options.map((o, i) => (
                      <div key={`poll-opt-input-${i}`} className="flex gap-2">
                        <Input
                          value={o}
                          onChange={(e) => setOptions(options.map((x, j) => (j === i ? e.target.value : x)))}
                          className="h-11"
                          placeholder={`Option ${i + 1}`}
                          data-testid={`poll-option-${i}`}
                        />
                        {options.length > 2 && (
                          <button type="button" onClick={() => setOptions(options.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive" data-testid={`remove-opt-${i}`}>
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => setOptions([...options, ""])} data-testid="add-poll-option-btn">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add option
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} className="rounded-full" data-testid="poll-submit-btn">
                    {saving ? "Creating..." : "Create poll"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-4">
        {items.length === 0 && (
          <div className="hoa-card p-10 text-center text-muted-foreground" data-testid="polls-empty">
            No polls yet.
          </div>
        )}
        {items.map((p) => {
          const total = (p.tally || []).reduce((s, n) => s + n, 0);
          return (
            <div key={p.id} className="hoa-card p-6" data-testid={`poll-${p.id}`}>
              <h3 className="font-semibold text-lg text-foreground mb-1">{p.question}</h3>
              <p className="text-xs text-muted-foreground mb-4">
                by {p.created_by} · {new Date(p.created_at).toLocaleDateString()} · {total} vote{total === 1 ? "" : "s"}
              </p>
              <div className="space-y-2">
                {p.options.map((opt, i) => {
                  const count = p.tally?.[i] || 0;
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  const mine = p.my_vote === i;
                  return (
                    <button
                      key={`${p.id}-opt-${i}`}
                      onClick={() => vote(p.id, i)}
                      className={`w-full text-left relative overflow-hidden rounded-lg border transition-colors ${
                        mine ? "border-primary bg-primary/5" : "border-border hover:bg-accent/40"
                      }`}
                      data-testid={`poll-${p.id}-option-${i}`}
                    >
                      <div className="absolute inset-0 bg-primary/10" style={{ width: `${pct}%` }} />
                      <div className="relative flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2">
                          {mine && <Check className="w-4 h-4 text-primary" />}
                          <span className="font-medium">{opt}</span>
                        </div>
                        <span className="font-mono text-sm text-muted-foreground">{pct}% · {count}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
